import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";
import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Button,
  Grid,
  Header,
  Card,
  Icon,
  Dropdown,
  Divider,
} from "semantic-ui-react";
import { DatePicker } from "@components/DatePicker";

class TopBorrowersList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fromDate: "",
      toDate: "",
      isExporting: false,
      exportFormat: "csv",
    };
  }

  componentDidMount() {
    this.fetchBorrowers();
  }

  get subtitle() {
    const { fromDate, toDate } = this.state;
    const { data } = this.props;
    const total = data.total || 0;
    const msg = `Showing ${total} top borrowers`;

    if (fromDate && toDate) {
      return `${msg} between ${fromDate} and ${toDate}.`;
    } else if (fromDate) {
      return `${msg} starting from ${fromDate}.`;
    } else if (toDate) {
      return `${msg} ending at ${toDate}.`;
    } else {
      return `${msg}.`;
    }
  }

  fetchBorrowers() {
    const { fromDate, toDate } = this.state;
    const { fetchTopBorrowers } = this.props;
    fetchTopBorrowers(fromDate, toDate);
  }

  handleFromDateChange = (value) => {
    this.setState({ fromDate: value }, this.fetchBorrowers);
  };

  handleToDateChange = (value) => {
    this.setState({ toDate: value }, this.fetchBorrowers);
  };

  handleExport = () => {
    const { exportFormat } = this.state;
    if (exportFormat === "csv") {
      this.exportCSV();
    } else if (exportFormat === "pdf") {
      this.exportPDF();
    }
  };

  exportCSV = () => {
    this.setState({ isExporting: true });
    try {
      const { data } = this.props;
      const hits = data.hits || [];
      const top10Records = hits.slice(0, 10);

      const headers = ["Top Place", "Name", "Loan Count"];
      const csvContent = [
        headers.join(","),
        ...top10Records.map((patron, i) =>
          [
            `Top ${i + 1}`,
            `"${(patron.name || "").replace(/"/g, '""')}"`,
            patron.loan_count || 0,
          ].join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `top-borrowers-${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("CSV export failed:", error);
    } finally {
      this.setState({ isExporting: false });
    }
  };

  exportPDF = () => {
    this.setState({ isExporting: true });
    try {
      const { data } = this.props;
      const { fromDate, toDate } = this.state;
      const hits = data.hits || [];
      const top10Records = hits.slice(0, 10);

      const date = new Date().toLocaleString();
      const reportTitle = "Top Borrowers Report";

      let dateRangeInfo = "";
      if (fromDate && toDate) {
        dateRangeInfo = ` (${fromDate} to ${toDate})`;
      } else if (fromDate) {
        dateRangeInfo = ` (from ${fromDate})`;
      } else if (toDate) {
        dateRangeInfo = ` (up to ${toDate})`;
      }

      const tableHeaders = `
        <th style="text-align: center; width: 15%;">Top</th>
        <th style="text-align: center; width: 60%;">Name</th>
        <th style="text-align: center; width: 25%;">Loan Count</th>
      `;

      const tableRows = top10Records
        .map((patron, i) => {
          const position = i + 1;

          return `
            <tr>
              <td style="text-align: center; font-weight: ${
                position <= 3 ? "bold" : "normal"
              };">
                ${position}
              </td>
              <td style="padding-left: 12px;">
                ${patron.name || "N/A"}
              </td>
              <td style="text-align: center;">
                ${patron.loan_count || 0}
              </td>
            </tr>
          `;
        })
        .join("");

      const htmlContent = generateExportHTML({
        reportTitle: reportTitle + dateRangeInfo,
        generatedDate: date,
        tableHeaders,
        tableRows,
        footerText: "Library Management System - Top Borrowers Export",
        fallbackLogoText: "Library Resource Center",
        showUniversityLogo: true,
        showLrcLogo: true,
      });

      const printWindow = window.open("", "_blank");
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
    } catch (error) {
      console.error("PDF export failed:", error);
    } finally {
      this.setState({ isExporting: false });
    }
  };

  renderFilterCard() {
    const { fromDate, toDate, isExporting, exportFormat } = this.state;
    const { data } = this.props;
    const hasData = data && data.hits && data.hits.length > 0;

    return (
      <Card fluid>
        <Card.Content style={{ paddingTop: "8px" }}>
          <Card.Header>
            <Icon name="calendar alternate outline" />
            Filter Top Borrowers
          </Card.Header>
          <Card.Meta>
            Select a date range to view borrowing statistics
          </Card.Meta>
        </Card.Content>
        <Card.Content style={{ paddingTop: "8px" }}>
          {" "}
          <Grid stackable>
            <Grid.Row>
              <Grid.Column width={8}>
                <div style={{ marginBottom: "1px" }}>
                  <strong>From Date</strong>
                </div>
                <DatePicker
                  maxDate={toDate}
                  placeholder="Select start date"
                  handleDateChange={this.handleFromDateChange}
                />
              </Grid.Column>
              <Grid.Column width={8}>
                <div style={{ marginBottom: "1px" }}>
                  <strong>To Date</strong>
                </div>
                <DatePicker
                  minDate={fromDate}
                  placeholder="Select end date"
                  handleDateChange={this.handleToDateChange}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Card.Content>
        <Card.Content extra>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ color: "#225745" }}>
              <Icon name="info circle" />
              {hasData
                ? `${data.total || 0} Borrowers Found`
                : "No data available"}
            </span>
            <Button.Group>
              <Dropdown
                button
                basic
                floating
                options={[
                  { key: "csv", value: "csv", text: "CSV", icon: "table" },
                  { key: "pdf", value: "pdf", text: "PDF", icon: "file pdf" },
                ]}
                value={exportFormat}
                onChange={(e, { value }) =>
                  this.setState({ exportFormat: value })
                }
                selectOnBlur={false}
                icon={null}
                text={exportFormat.toUpperCase()}
              />
              <Button
                primary
                icon
                labelPosition="left"
                onClick={this.handleExport}
                disabled={!hasData || isExporting}
                loading={isExporting}
                data-test="export-button"
              >
                <Icon name="download" />
                Export
              </Button>
            </Button.Group>
          </div>
        </Card.Content>
      </Card>
    );
  }

  renderBorrowerCards(data) {
    const hits = data.hits || [];

    if (hits.length === 0) {
      return (
        <Card fluid>
          <Card.Content textAlign="center">
            <Icon
              name="inbox"
              size="huge"
              style={{ color: "#ccc", margin: "20px 0" }}
            />
            <Header as="h3" style={{ color: "#999" }}>
              No borrowers found
            </Header>
            <p>Try adjusting your date range</p>
          </Card.Content>
        </Card>
      );
    }

    return (
      <>
        <Card.Group stackable itemsPerRow={3}>
          {hits.map((borrower, index) => {
            const position = index + 1;
            let medal = "";
            let cardColor = "grey";

            if (position === 1) {
              medal = "🥇";
              cardColor = "yellow";
            } else if (position === 2) {
              medal = "🥈";
              cardColor = "blue";
            } else if (position === 3) {
              medal = "🥉";
              cardColor = "orange";
            }

            return (
              <Card key={index} color={cardColor}>
                <Card.Content>
                  <Card.Header>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>Top {position}</span>
                      <Icon name="book" />
                    </div>
                  </Card.Header>
                  {medal && (
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "6em",
                        margin: "35px 13px -6px 101px",
                      }}
                    >
                      {medal}
                    </div>
                  )}
                  <Card.Meta>Borrower</Card.Meta>
                  <Card.Description
                    style={{ fontSize: "1.1em", marginTop: "8px" }}
                  >
                    <strong>{borrower.name || "N/A"}</strong>
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  <Icon name="bookmark" />
                  <strong>{borrower.loan_count || 0}</strong> loans
                </Card.Content>
              </Card>
            );
          })}
        </Card.Group>
      </>
    );
  }

  render() {
    const { data, isLoading, error } = this.props;

    return (
      <div style={{ padding: "20px" }}>
        {this.renderFilterCard()}
        <Divider hidden />
        <Loader isLoading={isLoading}>
          <Error error={error}>{this.renderBorrowerCards(data)}</Error>
        </Loader>
      </div>
    );
  }
}

TopBorrowersList.propTypes = {
  fetchTopBorrowers: PropTypes.func.isRequired,
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
};

TopBorrowersList.defaultProps = {
  isLoading: false,
  error: null,
  data: { hits: [], total: 0 },
};

export default TopBorrowersList;
